import api from '../../../../services/api';
import logger from '../../../../utils/logging/logger';

// Pure(ish) course/product data-fetching for useCourseProductFetch, extracted
// VERBATIM. Both functions throw well-formed Error objects (same
// .message/.status shape the original inline code threw) on failure —
// callers catch them exactly where the original inline try/catch blocks were,
// so error-message selection and side effects (setError/setLoading/
// sessionStorage cleanup) are unchanged.

// The productIds fetch path: parallel getAdditionalProduct calls, classified
// into success/failure, mapped into the course-like shape the rest of the
// hook expects.
export const fetchProductsList = async (identifiers, paymentApi = api) => {
  const productPromises = identifiers.map(id => {
    const productId = id.trim();
    return paymentApi.getAdditionalProduct(productId)
      .then(response => {
        // API returns { success: true, product: {...} }
        // Check for the expected structure: { success: true, product: {...} }
        if (response && response.success === true && response.product) {
          return { success: true, product: response.product, productId };
        }
        // Handle case where product is returned directly (shouldn't happen but be defensive)
        else if (response && (response.id || response.name)) {
          return { success: true, product: response, productId };
        }
        // Handle case where response structure is different
        else {
          return {
            success: false,
            error: 'Unexpected response format from server',
            productId,
            status: 500,
            response: response // Include response for debugging
          };
        }
      })
      .catch(error => {
        // Return a structured error response for better handling
        return {
          success: false,
          error: error.message || 'Product not found',
          productId,
          status: error.status || 500
        };
      });
  });
  const responses = await Promise.all(productPromises);

  // Separate successful responses from error responses
  const errorResponses = responses.filter(r => !r || r.success === false);
  const successfulResponses = responses.filter(r => r && r.success === true && r.product);

  // If we have some successful responses, use them even if there are errors
  if (successfulResponses.length > 0) {
    // Extract products from successful responses
    return successfulResponses.map(r => r.product).filter(product => product && (product.id || product.name));
  } else if (errorResponses.length > 0) {
    // All products failed - this is a real error
    const errorDetails = errorResponses.map(r => r?.productId || 'unknown').join(', ');
    const firstError = errorResponses[0];
    let errorMessage = firstError?.error || 'Product not found';

    // Provide more specific error messages based on status
    if (firstError?.status === 404) {
      errorMessage = `Product(s) with ID(s) ${errorDetails} not found. They may have been removed, are not active, or are no longer available for purchase.`;
    } else if (firstError?.status === 401 || firstError?.status === 403) {
      errorMessage = 'Authentication required. Please log in and try again.';
    } else if (!errorMessage.includes('Product')) {
      errorMessage = `Failed to fetch product information (IDs: ${errorDetails}). ${errorMessage}`;
    }

    const error = new Error(errorMessage);
    error.status = firstError?.status || 404;
    throw error;
  } else {
    // No responses at all - this shouldn't happen
    throw new Error('No product data received from server');
  }
};

// The non-numeric courseIds path: fetch the public catalog, filter down to
// the requested course codes, validate all were found, and shape the result.
// Pricing comes straight from the catalog response (no separate pricing calls).
export const fetchCoursesByCodesFromCatalog = async (identifiers, paymentApi = api) => {
  let allCoursesResponse;
  try {
    logger.info('Calling getPublicCourses API', { identifiers });
    allCoursesResponse = await paymentApi.getPublicCourses();
    logger.info('getPublicCourses API call completed', {
      hasResponse: !!allCoursesResponse,
      success: allCoursesResponse?.success,
      responseType: typeof allCoursesResponse,
      responseKeys: allCoursesResponse ? Object.keys(allCoursesResponse) : []
    });
  } catch (apiError) {
    logger.error('Error calling getPublicCourses API', {
      apiError,
      errorMessage: apiError.message,
      errorStatus: apiError.status,
      errorResponse: apiError.response,
      errorData: apiError.response?.data,
      errorStack: apiError.stack,
      identifiers
    });
    // Handle authentication errors specifically
    if (apiError.status === 401 || apiError.status === 403) {
      // FIX: Provide more helpful message for auth errors
      throw new Error('Your session has expired. Please refresh the page and try again. If the problem persists, please log out and log back in.');
    }
    // Handle network errors
    if (apiError.isNetworkError || apiError.isTimeout) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    // Generic error
    throw new Error(apiError.message || 'Failed to fetch course catalog. Please try again.');
  }

  // Check if response is valid
  if (!allCoursesResponse) {
    logger.error('Null response from getPublicCourses', { allCoursesResponse });
    throw new Error('Failed to fetch course catalog. The server returned no data. Please try again.');
  }

  if (!allCoursesResponse.success) {
    logger.error('Unsuccessful response from getPublicCourses', {
      allCoursesResponse,
      error: allCoursesResponse.error,
      message: allCoursesResponse.message
    });
    const errorMsg = allCoursesResponse.message || allCoursesResponse.error || 'Failed to fetch course catalog. Please try again.';
    throw new Error(errorMsg);
  }

  // Safely access courses array with fallback
  const allCourses = (allCoursesResponse && allCoursesResponse.courses) ? allCoursesResponse.courses : [];

  // FIX: Enhanced logging to diagnose catalog issues
  const catalogInfo = {
    hasResponse: !!allCoursesResponse,
    success: allCoursesResponse?.success,
    totalCourses: allCourses.length,
    identifiers,
    courseCodes: allCourses.map(c => ({ code: c.code, name: c.name, active: c.is_active })),
    allCourseCodesList: allCourses.map(c => c.code), // Simple list for easy scanning
    responseKeys: allCoursesResponse ? Object.keys(allCoursesResponse) : [],
    lookingFor: identifiers,
    foundInCatalog: identifiers.filter(code => allCourses.some(c => c.code === code))
  };

  logger.info('Catalog API response received', catalogInfo);

  if (allCourses.length === 0) {
    logger.warn('Catalog returned empty courses array', {
      response: allCoursesResponse,
      identifiers,
      message: 'This may indicate an authentication issue or the catalog is empty'
    });
  }

  const selectedCourses = allCourses.filter(course => {
    const matches = identifiers.includes(course.code);
    if (!matches && course.code) {
      logger.debug('Course code mismatch', {
        courseCode: course.code,
        identifiers,
        exactMatch: identifiers.some(id => id === course.code),
        caseSensitive: identifiers.some(id => id.toLowerCase() === course.code?.toLowerCase())
      });
    }
    return matches;
  });

  logger.info('Course filtering complete', {
    selectedCourses: selectedCourses.length,
    identifiers,
    foundCodes: selectedCourses.map(c => c.code),
    missingCodes: identifiers.filter(code => !selectedCourses.some(c => c.code === code)),
    allCourseCodes: allCourses.map(c => c.code)
  });

  // Check if all requested courses were found
  if (selectedCourses.length !== identifiers.length) {
    const foundCodes = selectedCourses.map(c => c.code);
    const missingCodes = identifiers.filter(code => !foundCodes.includes(code));

    // FIX: Provide more specific error message based on catalog state
    if (allCourses.length === 0) {
      logger.error('Catalog returned empty array', {
        identifiers,
        response: allCoursesResponse
      });
      throw new Error('The course catalog is currently empty. This may indicate an authentication issue or no courses are available for enrollment. Please refresh the page or contact support if the problem persists.');
    }
    logger.warn('Some courses not found in catalog', { missingCodes, foundCodes, identifiers });

    // FIX: Provide more helpful error message explaining why course might not be available
    const errorMsg = missingCodes.length === 1
      ? `Course "${missingCodes[0]}" is not available for enrollment. The course may be inactive or the enrollment period has ended. Please contact support if you believe this is an error.`
      : `The following courses are not available for enrollment: ${missingCodes.join(', ')}. These courses may be inactive or their enrollment periods have ended. Please contact support if you believe this is an error.`;
    throw new Error(errorMsg);
  }

  // Use pricing data directly from catalog response (already includes pricing)
  // No need to make separate pricing API calls
  // FIX: Add null checks to prevent errors if course data is incomplete
  const coursesData = selectedCourses.map(course => {
    if (!course || !course.id) {
      logger.error('Invalid course data in catalog response', { course });
      throw new Error('Invalid course data received from server. Please try again.');
    }
    return {
      id: course.id,
      name: course.name || 'Unnamed Course',
      description: course.description || '',
      code: course.code || '',
      instructor: course.instructor || 'TBA',
      price: course.price || 0,
      currency: course.currency || 'USD',
      isPaidCourse: course.isPaidCourse || false,
      maxStudents: course.maxStudents || null,
      startDate: course.startDate || null,
      endDate: course.endDate || null,
      is_active: course.is_active !== undefined ? course.is_active : true,
      openEnrollment: course.openEnrollment !== undefined ? course.openEnrollment : true
    };
  });

  // FIX #17: Validate currency consistency - all courses must use same currency
  const currencies = [...new Set(coursesData.map(c => c.currency || 'USD'))];
  if (currencies.length > 1) {
    logger.warn('Multiple currencies detected', { currencies, courses: coursesData.map(c => ({ name: c.name, currency: c.currency })) });
    // For now, log warning - backend should handle currency validation
    // In future, could show error or convert currencies
  }

  logger.debug('Course data from codes (using catalog pricing)', { coursesData });

  return coursesData;
};
